import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { MessageCircle, MoreVertical } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useAgentName } from '../../../lib/use-agent-name';
import { useAgentWorkspaceFiles } from '../../../lib/agent-workspace-files';
import { printDocument } from '../../../lib/doc-print';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLiveDocFile } from '../../../lib/use-live-doc-file';
import { useChatFilePaneEnabled } from '../../../lib/chat-file-pane-flag';
import { openDocViewer, updateOpenDocViewer } from '../../../lib/doc-viewer-presenter';
import { useDockBesideChat } from '../../../lib/chat-file-pane';
import type { DocViewerKind } from '../../../lib/doc-viewer';
import { buildFileActions, isViewerManageAction, type FileActionCallbacks } from '../../../lib/file-actions';
import { filePath, folderOf, isMemoryPath, type WriteDirectory } from '../../../lib/file-browser';
import { useFullContent, type WorkspaceFile } from '../../../lib/workspace-files';
import { Modal, useModalFullScreen } from '../../Modal';
import { Button, IconButton, OverflowMenu, type OverflowMenuAction } from '../ui';
import { FileActionsSheet } from '../files/FileActionsSheet';
import { FolderPickerDialog } from '../files/FolderPickerDialog';
import { FolderChooserDialog } from '../files/FolderChooserDialog';
import { ShareFileDialog } from '../files/ShareFileDialog';
import { FileAccessDialog } from '../files/FileAccessDialog';
import { ForgetMemoryDialog } from '../files/ForgetMemoryDialog';
import { fileSubtitle } from '../file-detail/FileDetailMeta';
import { FileActionButton } from '../file-detail/FileActionButton';
import { DocViewerBody } from './DocViewerBody';
import { DocEditView } from './DocEditView';
import { DocErrorState, DocShimmer } from './DocViewerStates';
import { docViewerBodyFit } from './body-class';
import styles from './DocViewerModal.module.css';

/** This shell's own class for each fit. `framed` (html) hides the second scrollbar since the sandboxed
 *  iframe scrolls itself; `compact` (audio) needs no special box here — the two-column body already sizes it. */
const BODY_FIT: Record<ReturnType<typeof docViewerBodyFit>, string> = {
  fill: '',
  framed: styles.bodyFramed,
  compact: '',
};

export interface DocViewerShellProps {
  file: WorkspaceFile;
  /** Which viewer to render, RESOLVED BY THE CALLER — never re-derived here.
   *
   *  The call sites classify with three inputs (name, url, mimeType); a `WorkspaceFile` carries only
   *  name + content, so re-deriving inside would drop the mime and answer `null` for exactly the
   *  attachment the mime rescue exists for (no name extension, no URL extension, `mimeType: text/html`) —
   *  a chip that suppressed the browser hand-off and then rendered nothing at all. The caller already
   *  knows the answer, so it passes it; the alternative (smuggling a `mimeType` onto `WorkspaceFile`)
   *  would put a field on the workspace-files contract that its endpoint never sends.
   *
   *  `null` means "we cannot preview this one" and is rendered as a state INSIDE the viewer, not routed
   *  elsewhere — so every file type has the same presentation and the same actions. */
  kind: DocViewerKind | null;
  /** The BI page_name the viewer's own actions report under. */
  pageName: string;
  /** This file's own page as a new-tab target, when the opener could name one — see `DocViewerRequest`. */
  fileHref?: string;
  /** The workspace the file belongs to, for the subtitle's third segment. Only the surfaces that know it
   *  pass it (the workspace gallery); absent it, the segment is omitted rather than fabricated. */
  workspaceName?: string;
  /** Whether this file lives in the account's file tree — the gate on Move to… / Send a copy / Share. See
   *  `DocViewerRequest.inFileTree`. */
  inFileTree?: boolean;
  /** Land in the editor rather than the reader — a row's pencil, which has no viewer of its own to open.
   *  See `DocViewerRequest.editing`. */
  startEditing?: boolean;
  /** Set when this viewer is CREATING a file rather than showing one — see `DocViewerRequest.draft`. The
   *  editor grows a name, a description and a destination, and the header's file actions go away: there
   *  is nothing yet to download, print, move, copy or share. */
  draft?: { folder: string };
  /** The file-system directory the editor saves back into — see `DocViewerRequest.directory`. */
  directory?: WriteDirectory;
  /** The file's workspace address, which keeps the open copy LIVE — see `DocViewerRequest.vfsPath`. */
  vfsPath?: string;
  /** The open request this viewer draws (`OpenDocViewer.id`). What the viewer learns while open — the live
   *  copy, whether the editor is open — is written back to it, so a pane↔modal move keeps it. */
  requestId?: number;
  onClose: () => void;
}

export interface DocViewerShell {
  /** The file AS IT IS NOW — the opened copy, replaced in place whenever live refresh reads a newer one. */
  file: WorkspaceFile;
  close: () => void;
  discuss: () => void;
  discussLabel: string;
  modalBi: (action: string) => void;
  /** The builder's own primary list, in its own order — what the phone sheet lists. */
  viewerActions: OverflowMenuAction[];
  /** The builder's SECOND group, Discuss — handed to the phone sheet as its trailing group only. */
  discussGroup: OverflowMenuAction[];
  /** The desktop cluster's halves: the byte actions as buttons, the filing actions behind a kebab. */
  utilityActions: OverflowMenuAction[];
  manageActions: OverflowMenuAction[];
  /** The reading surface (or the editor, while editing), already wrapped in the body box. */
  body: ReactNode;
  /** The filing dialogs the viewer hosts. Render them OUTSIDE the viewer's own card — see the modal. */
  dialogs: ReactNode;
}

/**
 * Everything the document viewer IS, minus the box it sits in. The `DocViewerModal` and the chat's
 * `DocViewerPane` are two presentations of one viewer, so the actions, the editor, the filing dialogs and
 * live refresh live here, once. A second copy would be a pane that silently lacks whatever the modal grows
 * next.
 */
export function useDocViewerShell({
  file: openedFile,
  kind,
  pageName,
  fileHref,
  inFileTree = false,
  startEditing,
  directory,
  onClose,
  draft,
  vfsPath,
  requestId,
}: DocViewerShellProps): DocViewerShell {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const agent = useAgentName();
  const { logModalActionBi, logPageClickBi } = useGenericBi();

  // The host re-keys the viewer on every open (`OpenDocViewer.id`), so this initial value is honoured per
  // open rather than only on the first one.
  const [editing, setEditing] = useState(startEditing === true);
  // Paused while editing, and never for a draft, which has no stored file to re-read yet. Live refresh ships
  // with the chat file pane, behind the same Conductor experiment — closed, the viewer keeps the copy it opened with.
  const live = useChatFilePaneEnabled();
  const dockBesideChat = useDockBesideChat();
  // A Files row carries only the HEAD of its body (`contentTruncated`). The whole file is read before the
  // body or the editor is drawn — an editor seeded from a head would save the head over the file.
  const full = useFullContent(openedFile);
  const loaded = full.status === 'ready' ? full.file : openedFile;
  const file = useLiveDocFile(loaded, draft || !live ? undefined : vfsPath, editing || full.status !== 'ready');
  // Write both back to the open request, so the other presentation mounts with them (see `updateOpenDocViewer`).
  useEffect(() => {
    if (requestId !== undefined && file !== openedFile) updateOpenDocViewer(requestId, { file });
    // `openedFile` is the request's own copy: after a write-back it IS `file`, which is what ends the loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, file]);
  useEffect(() => {
    if (requestId !== undefined) updateOpenDocViewer(requestId, { editing });
  }, [requestId, editing]);

  const modalBi = (action: string) => logModalActionBi({ page_name: pageName, modal_name: 'doc_viewer', action, answer: file.name });
  const close = () => {
    modalBi('close');
    onClose();
  };
  const discuss = () => {
    modalBi('discuss');
    const link = chatHandoffLink({ kind: 'new', message: t('agentCreations.detail.askMessage', { file: file.name }) }, basePath);
    void router.push(link.route, link.path);
    // Under the chat file pane experiment the file comes along: it docks beside the chat Discuss opens, so the
    // reader talks about the document with it still on screen. Where the chat cannot host a pane (narrow,
    // stacked) `useChatFilePane` keeps the same request in this modal, as before. Marking it is safe with the
    // experiment closed too — `useChatFilePane` then never docks it — so it does not wait on the gate.
    if (requestId !== undefined) {
      updateOpenDocViewer(requestId, { besideChat: true, handoff: true });
      return;
    }
    // A viewer its opener renders directly (`SharedFileViewer`) has no request to mark, and unmounts with the
    // screen Discuss leaves — so it hands the file to the presenter, beside the chat, and closes itself.
    dockBesideChat(() => {
      openDocViewer({ file, kind, pageName, fileHref, inFileTree, directory, vfsPath, besideChat: true, handoff: true });
      onClose();
    });
  };

  // ═══ THE FILING DIALOGS THE VIEWER NOW OWNS ══════════════════════════════════════════════════════
  //
  // Move to… / Send a copy / Share (grant access) used to be reachable ONLY from a Files row: `buildFileActions`
  // gated all three on `context === 'row'`, so the open document's kebab offered three actions where the row
  // behind it offered seven — on web, iOS and Android at once. They are the viewer's now, and it hosts their
  // three dialogs itself.
  //
  // ⚠️ It hosts them RATHER THAN asking `FilesBrowser` for them, and it has to: the viewer is mounted ONCE at
  // the root (`DocViewerHost`, or the chat's pane), above every page, and is opened just as often from a chat
  // attachment or a dashboard card as from the Files list. There is frequently no `FilesBrowser` mounted at
  // all. Each dialog is self-contained — it takes the file (or its id + path) and does its own data fetching
  // — so owning them here costs nothing and closes the gap on every opener at once.
  //
  // They are gated on `inFileTree`, NOT on width and NOT on the opener's identity: a chat attachment has no
  // folder to move between, no stored body to copy, and no `FileSystem.File.id` to grant against, so all
  // three rows are absent there instead of opening a dialog that can only fail. See that field's doc.
  const [moving, setMoving] = useState(false);
  const [forgetting, setForgetting] = useState(false);
  const [sendingCopy, setSendingCopy] = useState(false);
  const [granting, setGranting] = useState(false);

  // `onPrint` does NOT log BI itself — `FileActionButton` already fires `modalBi('print')` for the header
  // entry. Discuss is the exception: it is rendered as the footer primary (or, on a phone, the header
  // bubble) rather than via `FileActionButton`, so its own handler logs.
  // `printDocument` prints our own page unless the view on screen claimed the job — a PDF does, because the
  // parent print job cannot paginate its embed. See `lib/doc-print.ts`.
  //
  // ⚠️ `onGrantAccess` is passed UNCONDITIONALLY (for a filed file) — no width read. On the row it is narrow-only,
  // because a wide row grows a dedicated share `IconButton` a named entry would duplicate; the viewer's bar
  // draws no share glyph at EITHER width, so there is nothing to duplicate and the question does not arise.
  const callbacks: FileActionCallbacks = {
    onDiscuss: discuss,
    // Print prints what is on screen, and until the whole body has been read that is a shimmer or an error —
    // so it is withheld rather than offered against a preview. Download reads the whole file itself.
    ...(full.status === 'ready' ? { onPrint: printDocument } : {}),
    ...(inFileTree
      ? {
          onMove: () => setMoving(true),
          onSendCopy: () => setSendingCopy(true),
          onGrantAccess: () => setGranting(true),
          // A memory can be forgotten from inside the viewer, as on both phones; `buildFileActions` still
          // withholds the entry on anything that is not a memory row, so wiring it here is safe for every file.
          ...(isMemoryPath(filePath(file)) ? { onDelete: () => setForgetting(true) } : {}),
        }
      : {}),
    // Editing happens INSIDE the viewer rather than in a modal of its own: the file is already open and
    // already the whole card, so a second layer would only take the document further away.
    //
    // Withheld while the editor is open — it already owns the controls that matter there (Edit/Preview,
    // Cancel, Save), and a pencil that re-enters the mode you are in is a control that does nothing.
    // The editor refuses a head on its own (`DocEditView`), and offering the pencil before the body lands
    // would only open that refusal.
    onEdit: editing || full.status !== 'ready'
      ? undefined
      : () => {
          modalBi('edit');
          setEditing(true);
        },
  };
  // ONE action set for this screen, split by width into two presentations — the same rule `FileRowMenu`
  // applies to the row, and the reason the split is a KEY list in `lib/file-actions.ts` rather than a second
  // `context`: the width question is asked once, by the presentation, so the two halves cannot disagree
  // about what exists.
  //
  //   - `utilityActions` — how to get at the document's bytes (Download / Print / Open in new tab, or the
  //     wixel Open/Edit/Download trio). Figma `15652:57057` draws exactly these as the desktop header's
  //     labelled buttons.
  //   - `manageActions`  — what to do with the document (Move to… / Send a copy / Share). On a desktop these
  //     ride a kebab in the same cluster: six labelled buttons beside a file name is not the design, and the
  //     three that got cut are the three a reader reaches for least often mid-read. On a PHONE
  //     (`15897:424580`) there is no cluster at all — every action is in the one bottom sheet.
  //
  // The builder's SECOND group is Discuss, and it is KEPT — handed to the phone sheet as its own trailing
  // group (see the modal's header note for why the bubble and the row are not a duplication). It is
  // deliberately not folded into either half above: `utilityActions` / `manageActions` are the DESKTOP
  // cluster's two halves, and leaving Discuss out of the list they filter is the whole mechanism that keeps
  // the wide popover clean.
  const [viewerActions, discussGroup] = buildFileActions(file, kind, callbacks, { context: 'viewer', t, agentName: agent.short, fileHref });
  const utilityActions = viewerActions.filter((action) => !isViewerManageAction(action));
  const manageActions = viewerActions.filter(isViewerManageAction);

  const discussLabel = t('agentCreations.detail.discuss', { agentName: agent.short });

  // The destination picker for a DRAFT. It is a question, so it is modelled as one: the editor awaits a
  // folder and the dialog resolves it, rather than the two sharing a pile of state. `null` means dismissed.
  const [choosingFolder, setChoosingFolder] = useState<{ from: string; resolve: (folder: string | null) => void } | null>(null);
  const pickFolder = (): Promise<string | null> =>
    new Promise((resolve) => setChoosingFolder({ from: draft?.folder ?? folderOf(filePath(file)), resolve }));
  // The editor names the file's destination whether it is new or not, so the Space names are needed in
  // both modes. It is the account-wide list every Files surface already reads, so this is a cache hit
  // wherever the viewer was opened from a file list.
  const workspaceFiles = useAgentWorkspaceFiles();
  const workspaceNames = useMemo(
    () => Object.fromEntries(workspaceFiles.status === 'ready' ? workspaceFiles.items.map((i) => [i.workspaceId, i.workspaceName]) : []),
    [workspaceFiles],
  );

  const body = (
    // `orion-doc-viewer-body` is a plain global marker (not a CSS-module class) — see globals.css for the
    // print-only rule it hooks into `@media print`. The per-kind fit modifier lives in `docViewerBodyFit`,
    // shared with the standalone page so the two hosts cannot drift.
    <div className={`${styles.body} orion-doc-viewer-body ${BODY_FIT[docViewerBodyFit(kind)]}`}>
      {full.status === 'loading' ? (
        <DocShimmer />
      ) : full.status === 'error' ? (
        <DocErrorState onRetry={full.retry} />
      ) : editing ? (
        <DocEditView
          file={file}
          kind={kind}
          directory={directory}
          onBi={modalBi}
          // Cancelling a DRAFT leaves the viewer — there is no saved document behind it to fall back to,
          // where cancelling an edit returns to reading the file.
          onClose={draft ? close : () => setEditing(false)}
          workspaceNames={workspaceNames}
          onPickFolder={pickFolder}
          draft={draft && { startFolder: draft.folder }}
        />
      ) : (
        <DocViewerBody
          file={file}
          kind={kind}
          onBi={modalBi}
          onFirstVideoPlay={() =>
            logPageClickBi({ page_name: pageName, button_name: 'video_played' }, { additionalFields: { file_name: file.name } })
          }
        />
      )}
    </div>
  );

  const dialogs = (
    <>
      {/* Each is its own `Modal`, so it registers last on the shared Escape stack and one Escape dismisses
          it before the viewer under it. */}
      {moving && <FolderPickerDialog item={file} onClose={() => setMoving(false)} />}
      {/* Forgetting closes the viewer: the document it showed no longer exists. The listing behind it refreshes
          through `useFileDelete`'s invalidation, the same way the row path does. */}
      {forgetting && (
        <ForgetMemoryDialog
          item={file}
          onClose={() => setForgetting(false)}
          onForgotten={() => {
            setForgetting(false);
            close();
          }}
        />
      )}
      {choosingFolder && (
        <FolderChooserDialog
          startFolder={choosingFolder.from}
          title={t('files.new.locationLabel')}
          confirmLabel={t('files.new.locationConfirm')}
          onClose={() => {
            choosingFolder.resolve(null);
            setChoosingFolder(null);
          }}
          onChoose={(folder) => {
            choosingFolder.resolve(folder);
            setChoosingFolder(null);
          }}
        />
      )}
      {sendingCopy && <ShareFileDialog item={file} onClose={() => setSendingCopy(false)} />}
      {/* Every file can be granted. A file older than the id field has none, and the server mints one from
          the path at share time — which is why the path travels alongside whatever id exists. The path is the
          file's full `workspaces` key, exactly as `FilesBrowser` builds it. */}
      {granting && (
        <FileAccessDialog
          fileId={file.fileId ?? ''}
          filePath={`${file.workspaceId ?? ''}/${file.name}`}
          fileName={file.name}
          onClose={() => setGranting(false)}
        />
      )}
    </>
  );

  return { file, close, discuss, discussLabel, modalBi, viewerActions, discussGroup, utilityActions, manageActions, body, dialogs };
}

// The in-app document viewer. EVERY file opens here — a report, a sheet, an image, a video, a PDF, a wixel
// asset, and equally a `.docx` we cannot render, which gets a no-preview state in the same shell rather
// than being handed to the browser or routed to another page. One presentation, one set of actions, so
// "what happens when I click a file" no longer depends on which list you clicked from.
//
// Built on the shared `Modal` shell so it inherits the portal, Escape, scrim, RailCatcher and footer slot,
// plus a `cardClassName` that bounds the card to the viewport (see the module CSS). Not a route: a route
// would put the signed WixMP URL into browser history and the referrer, and the chat surface underneath is
// an overlay the user must not lose.
//
// Layout follows the Figma redesign (node 15652:57057), sized up to the DS `maximum` width so the document
// gets most of the screen:
//
//   - the HEADER is the file name over a `built by … · {space}` subtitle (each segment shown only when its
//     datum exists, and no subtitle at all when neither does), with the utility actions — Download / Print /
//     Open in new tab, from the ONE shared `buildFileActions` — as the row's TRAILING cluster, followed by a
//     kebab holding the FILING actions (Move to… / Send a copy / Share). They ride `Modal`'s `headerActions`
//     slot rather than markup of our own: the actions-beside-the-title row is overlay chrome, so it belongs
//     to the shell (see that prop's doc).
//   - the BODY is the `DocViewerBody` reading surface, capped to the viewport with its own internal scroll,
//     and 17 under the header rule with NOTHING below it — the metadata caption that used to sit there is
//     gone, its content superseded by the header subtitle (native dropped the same row).
//   - the FOOTER carries one control: the Discuss primary, right-aligned under its own rule.
//
// **On a PHONE it is a different bar, not a narrower one** (node 15897:424580, the mobile presenter — the
// shape both native apps now draw): `[circular ✕] [name / subtitle] … [discuss bubble] [kebab]`, over a
// full-bleed rule, with the document inset 20 and 27 below it. The labelled buttons do not fit a 390px row,
// so the design collapses the WHOLE action set into the one kebab (presented as the phone's bottom sheet —
// `FileActionsSheet`, which is what both phones open too) and promotes Discuss out of the footer and onto the
// bar as a bubble. The footer therefore goes away down there: the footer's one control is now on the bar.
// `Modal`'s `useModalFullScreen()` is the ONE reader of the width — the shell reshapes the header off the
// same boolean.
//
// **On a PHONE, Discuss is BOTH the header bubble AND the sheet's last row — deliberately, and this is the
// shape all three surfaces agree on.** The bubble is the SHORTCUT (reading a report and wanting to ask about
// it is one motion); the row is the INVENTORY. A drawer that collapses the whole action set into itself and
// then lists every single thing you can do with the document EXCEPT the one you most want reads as
// incomplete — the user goes looking for it in the menu, because the menu is where "everything else" lives.
// Both phones draw exactly this (iOS `FileActionSheet(onDiscuss:)` from `DocumentViewer`, Android
// `FileActionMenu(onDiscuss = …)`), and web's own Files-ROW sheet has listed it under the same rule all
// along (`… Open in new tab · Share ── Discuss it with {agent}`). It is LAST, in its OWN group, so
// `FileActionsSheet`'s between-groups rule falls above it — no hand-rolled `<hr>`.
//
// **The DESKTOP popover stays free of it, structurally rather than by a second width read.** That menu
// renders `manageActions`, a filter of the builder's PRIMARY group; the Discuss group is only ever handed to
// the sheet. So there is no width test to get wrong: the wide cluster cannot see a row that never enters the
// list it filters. On a desktop Discuss remains the footer primary, one Tab away and permanently on screen,
// which is why the row would genuinely be redundant there and is not down here.
//
// **The viewer itself — actions, editor, filing dialogs, live refresh — is `useDocViewerShell` above.** The
// component below is only its MODAL presentation; the chat's `DocViewerPane` is the other one, so the two
// cannot drift.
export function DocViewerModal(props: DocViewerShellProps) {
  const { workspaceName, draft } = props;
  const { t } = useTranslation();
  const agent = useAgentName();
  const { file, close, discuss, discussLabel, modalBi, viewerActions, discussGroup, utilityActions, manageActions, body, dialogs } =
    useDocViewerShell(props);

  // THE one reader of the width for this screen — the shell reshapes the header off the same boolean, so
  // the controls and the box they sit in can never disagree. See `useModalFullScreen`.
  const phone = useModalFullScreen();
  const [actionsOpen, setActionsOpen] = useState(false);

  return (
    <>
      <Modal
        open
        onClose={close}
        title={draft ? t('files.new.title') : file.name}
        description={fileSubtitle(file, t, { workspaceName, assistantName: agent.short })}
        labelledBy="doc-viewer-title"
        size="maximum"
        headerSeparator
        // No rule under a document with no footer beneath it — on a phone there is nothing for it to
        // separate, and it would read as an unfinished second header.
        footerSeparator={!phone}
        cardClassName={styles.card}
        contentClassName={styles.modalContent}
        headerActions={
          phone ? (
            <>
              {/* The discuss BUBBLE — the footer primary's job, promoted onto the bar: reading a report and
                  wanting to ask about it is one motion. 42px box, 24px glyph, ghost fill (the shell's
                  `.headerActions` supplies the 2 between the pair); `size="large"` IS the 42/radius-8 box. */}
              <IconButton size="large" aria-label={discussLabel} onClick={discuss}>
                <MessageCircle size={24} aria-hidden />
              </IconButton>
              {/* …and the kebab, holding the WHOLE action set — the utility half the desktop header spells out
                  as buttons AND the filing half its own kebab holds. There is no cluster to split down here,
                  so there is nothing to split: one sheet, every action, which is exactly what both phones'
                  viewers draw. A bottom sheet rather than a popover: a popover anchored to a 42px control on a
                  phone opens under the thumb that summoned it and has no room to name the file — see
                  `FileActionsSheet`, which is also what both native apps open here. */}
              <IconButton
                size="large"
                aria-label={t('agentCreations.table.options')}
                aria-haspopup="dialog"
                onClick={() => {
                  modalBi('more_actions');
                  setActionsOpen(true);
                }}
              >
                <MoreVertical size={24} aria-hidden />
              </IconButton>
            </>
          ) : (
            <>
              {utilityActions.map((action) => <FileActionButton key={action.key} action={action} onBi={modalBi} />)}
              {/* The filing half, behind a kebab in the same cluster. Figma `15652:57057` draws the trio above
                  as this header's whole content, and it is right about that: six labelled buttons beside a file
                  name is a toolbar, not a header. So the actions a reader reaches for least often mid-read ride
                  an overflow — the same wide-popover / narrow-sheet split `FileRowMenu` gives the row, and the
                  same `OverflowMenu` primitive. Omitted entirely when there is nothing to put in it (a chat
                  attachment, a wixel asset with no folder), never rendered as an empty menu. */}
              {manageActions.length > 0 && (
                <OverflowMenu
                  groups={[manageActions]}
                  ariaLabel={t('agentCreations.table.options')}
                  triggerIcon={<MoreVertical size={16} aria-hidden />}
                  onOpen={() => modalBi('more_actions')}
                />
              )}
            </>
          )
        }
        footer={
          /* One control, right-aligned by `Modal`'s own footer — so no bar, no groups, no wrapper. No Close
             button either: the modal already closes three ways (the header ✕, Escape, a scrim click) and a
             fourth here only competed with Discuss for the corner where the primary action belongs.
             GONE on a phone: node `15897:424580` has no footer, because Discuss is the header bubble there
             and two Discusses on one screen is worse than none. */
          phone ? undefined : <Button onClick={discuss}>{discussLabel}</Button>
        }
      >
        <div className={styles.layout}>{body}</div>
      </Modal>
      {/* OUTSIDE the viewer's own card, beside it — the same rule `FileActionsSheet` states for the Files
          rows: a portal does not escape React's event bubbling, so a scrim press inside the card's subtree
          would bubble into it. It is its own `Modal`, so it registers LAST on the shared Escape stack and
          one Escape dismisses the sheet before the viewer under it. */}
      <FileActionsSheet
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        title={draft ? t('files.new.title') : file.name}
        /* ⚠️ `viewerActions` — the builder's own list, NOT `[...utilityActions, ...manageActions]`. Those two
           are a filter of this list for the DESKTOP cluster's benefit; concatenating them back rewrites the
           order, which is how a first pass here shipped
           `Download · Print · Open in new tab · Move to… · Send a copy · Share` — Open-in-new-tab jumped two
           places ahead of the slot Figma `15910:425995` and both phones give it.
           …then `discussGroup` as a SECOND group, which is what draws the rule above it: `FileActionsSheet`
           renders an `<hr>` BETWEEN groups only, so the divider Figma `15910:426010` puts above Discuss is
           the group boundary itself — never a rule of our own. Two groups, in this order, is the same shape
           the Files-row sheet and both phones' viewers draw. */
        groups={[viewerActions, discussGroup]}
      />
      {/* The filing dialogs, hosted by the shell — OUTSIDE the viewer's card for the same event-bubbling
          reason `FileActionsSheet` is. */}
      {dialogs}
    </>
  );
}
